import { ReactElement, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { Box, Skeleton, Typography } from '@mui/material';
import { NavigateNext as BreadcrumbsSeparator, MoreHoriz as MoreIcon } from '@mui/icons-material';
import FolderIcon from '@mui/icons-material/Folder';
import { useDocumentationStore } from 'store/documentation';
import { Color } from 'shared/utils/colors';
import { BreadcrumbItem } from 'store/documentation/slices/navigation/types';
import { useAppNavigation } from 'contexts/navigation';
import { useProjectId } from 'store/project';

const BREADCRUMB_BAR_HEIGHT = 52;
const BREADCRUMB_BAR_MAX_WIDTH = 600;
const VISIBLE_BREADCRUMBS_WHEN_COLLAPSED = 3; // First + last 2
const VERTICAL_OVERFLOW = 4; // How much the bar extends above/below the row

interface BreadcrumbHoverBarProps {
  visible: boolean;
}

export function BreadcrumbHoverBar({ visible }: BreadcrumbHoverBarProps): ReactElement | null {
  const intl = useIntl();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const hoveredBreadcrumbs = useDocumentationStore.use.hoveredBreadcrumbs();
  const isLoadingHoveredBreadcrumbs = useDocumentationStore.use.isLoadingHoveredBreadcrumbs();
  const hoveredRowTop = useDocumentationStore.use.hoveredRowTop();
  const hoveredCellRight = useDocumentationStore.use.hoveredCellRight();
  const navigateToDirectoryFromSearch = useDocumentationStore.use.navigateToDirectoryFromSearch();
  const clearHoveredBreadcrumbs = useDocumentationStore.use.clearHoveredBreadcrumbs();
  const projectId = useProjectId();
  const { goToDocumentation } = useAppNavigation();

  const rootBreadcrumb: BreadcrumbItem = useMemo(() => ({
    _id: '',
    name: formatTranslation(intl, CommonTranslationKeys.documentation),
  }), [intl]);

  const displayBreadcrumbs = useMemo((): BreadcrumbItem[] => {
    return [rootBreadcrumb, ...hoveredBreadcrumbs];
  }, [rootBreadcrumb, hoveredBreadcrumbs]);

  // Determine if we need to collapse breadcrumbs
  const shouldCollapse = displayBreadcrumbs.length > VISIBLE_BREADCRUMBS_WHEN_COLLAPSED;
  const collapsedCount = shouldCollapse ? displayBreadcrumbs.length - VISIBLE_BREADCRUMBS_WHEN_COLLAPSED : 0;

  // When collapsed: show first breadcrumb, "...", and last 2 breadcrumbs
  const visibleBreadcrumbs = useMemo((): BreadcrumbItem[] => {
    if (!shouldCollapse || isExpanded) {
      return displayBreadcrumbs;
    }
    // First breadcrumb + last 2
    return [
      displayBreadcrumbs[0],
      ...displayBreadcrumbs.slice(-2),
    ];
  }, [displayBreadcrumbs, shouldCollapse, isExpanded]);

  // Reset expanded state when breadcrumbs change or become invisible
  useEffect(() => {
    if (!visible) {
      setIsExpanded(false);
    }
  }, [visible, hoveredBreadcrumbs]);

  // Scroll to end when expanded or when breadcrumbs change (to ensure last item is visible)
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
    }
  }, [isExpanded, hoveredBreadcrumbs]);

  const handleBreadcrumbClick = useCallback(
    (id: string, name: string) => {
      if (!id) {
        goToDocumentation(projectId, { replace: true });
      }
      // No early return above: navigateToDirectoryFromSearch must still run for
      // root (id === '') too, since it also preserves the active search phrase
      // (see navigateToDirectoryFromSearch) so it can be restored later.
      navigateToDirectoryFromSearch(id, name);
      clearHoveredBreadcrumbs();
    },
    [
      navigateToDirectoryFromSearch,
      clearHoveredBreadcrumbs,
      goToDocumentation,
      projectId,
    ]
  );

  const handleExpandClick = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearHoveredBreadcrumbs();
    setIsExpanded(false);
  }, [clearHoveredBreadcrumbs]);

  if (!visible) {
    return null;
  }

  // Position bar to extend slightly above and below the row
  const ROW_HEIGHT = 52; // typical DataGrid row height
  const verticalOffset = (ROW_HEIGHT - BREADCRUMB_BAR_HEIGHT) / 2 - VERTICAL_OVERFLOW;
  const topPosition = (hoveredRowTop ?? 0) + verticalOffset;
  
  // Calculate right offset so that last breadcrumb text aligns exactly with cell text
  // Need to account for: container padding (12px) + item padding (12px) = 24px
  const PADDING_OFFSET = 24;
  const rightOffset = hoveredCellRight !== null 
    ? `calc(100% - ${hoveredCellRight}px - ${PADDING_OFFSET}px)` 
    : 0;

  const renderBreadcrumbButton = (breadcrumb: BreadcrumbItem, isLast: boolean) => (
    <Box
      component="button"
      onClick={() => handleBreadcrumbClick(breadcrumb._id, breadcrumb.name)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: '8px 12px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: isLast ? 'rgba(0,0,0,0.08)' : 'transparent',
        color: 'text.primary',
        cursor: 'pointer',
        fontWeight: isLast ? 700 : 400,
        fontSize: '0.875rem',
        fontFamily: 'Archivo, sans-serif',
        transition: 'background-color 0.15s',
        flexShrink: 0,
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.08)',
        },
      }}
    >
      <FolderIcon
        sx={{
          fontSize: 20,
          color: Color.iconGray,
        }}
      />
      <Typography
        variant="body2"
        component="span"
        sx={{
          whiteSpace: 'nowrap',
          fontWeight: 'inherit',
        }}
      >
        {breadcrumb.name}
      </Typography>
    </Box>
  );

  const renderExpandButton = () => (
    <Box
      component="button"
      onClick={handleExpandClick}
      title={formatTranslation(intl, DocumentationTranslationKeys.breadcrumbExpandHint, { count: collapsedCount })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 8px',
        borderRadius: '6px',
        border: 'none',
        backgroundColor: 'transparent',
        color: 'text.secondary',
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontFamily: 'Archivo, sans-serif',
        transition: 'background-color 0.15s',
        flexShrink: 0,
        minWidth: 36,
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.08)',
        },
      }}
    >
      <MoreIcon sx={{ fontSize: 20 }} />
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'absolute',
        top: topPosition,
        right: rightOffset,
        zIndex: 1000,
        maxWidth: BREADCRUMB_BAR_MAX_WIDTH,
        backgroundColor: '#f8f9fa',
        border: '1px solid #dadce0',
        borderRadius: '8px',
        boxShadow: `0 1px 3px ${Color.border}, 0 1px 2px rgba(0,0,0,0.24)`,
        padding: '6px 12px',
        height: BREADCRUMB_BAR_HEIGHT,
      }}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: 0,
          // Hide scrollbar for cleaner look
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            height: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 2,
          },
        }}
      >
        {isLoadingHoveredBreadcrumbs ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
            <Skeleton variant="circular" width={18} height={18} />
            <Skeleton variant="text" width={60} height={20} />
            <BreadcrumbsSeparator sx={{ color: '#9aa0a6', fontSize: 18 }} />
            <Skeleton variant="text" width={50} height={20} />
          </Box>
        ) : (
          visibleBreadcrumbs.map((breadcrumb, index) => {
            const isLast = index === visibleBreadcrumbs.length - 1;
            const isFirstInCollapsedView = index === 0 && shouldCollapse && !isExpanded;
            
            return (
              <Box
                key={breadcrumb._id || 'root'}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {renderBreadcrumbButton(breadcrumb, isLast)}
                
                {/* Show expand button after first breadcrumb when collapsed */}
                {isFirstInCollapsedView && (
                  <>
                    <BreadcrumbsSeparator
                      sx={{
                        color: Color.lightGray,
                        fontSize: 20,
                        mx: 0,
                        flexShrink: 0,
                      }}
                    />
                    {renderExpandButton()}
                  </>
                )}
                
                {!isLast && (
                  <BreadcrumbsSeparator
                    sx={{
                      color: Color.lightGray,
                      fontSize: 20,
                      mx: 0,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
